import React, { useMemo, useCallback, FocusEventHandler } from 'react'

import { useField } from 'formik'
import { useDeepCompareMemo } from 'use-deep-compare'

import { FieldSettings, FormHelpersProviderReturn } from '../../../ana-toolbox'
import { getHasError } from '../../utils/helpers/getHasError'
import { useFormHelpers } from '../../utils/hooks/useFormHelpers'
import { useSubmitCount } from '../../utils/hooks/useSubmitCount'
import { combinedFields } from '../../utils/yup/main'
import { TextInput } from '../text-input/text-input'

import { TextFieldProps } from './text-field.types'

export const TextField = ({ onBlur, onFocus, ...props }: TextFieldProps) => {
  const submitCount = useSubmitCount()
  const formHelpers = useFormHelpers()
  const [field, meta] = useField(props.name)

  const hasError = useMemo(() => getHasError(meta, submitCount ?? 1), [meta, submitCount])

  const memoizedField = useDeepCompareMemo(() => field, [field])

  const fieldSettings = useMemo(
    () => combinedFields[props.name as keyof typeof combinedFields] as FieldSettings,
    [props.name]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      fieldSettings?.hooks?.beforeChange?.(e.target.value, formHelpers as FormHelpersProviderReturn, e)
      field.onChange(e)
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleBlur: FocusEventHandler = useCallback(
    (e) => {
      memoizedField.onBlur(e)
      onBlur?.(e)
    },
    [onBlur, memoizedField]
  )

  const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      onFocus?.(e)
    },
    [onFocus]
  )

  return (
    <TextInput
      error={hasError ? meta.error : undefined}
      {...props}
      {...memoizedField}
      value={memoizedField.value || ''}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onChange={handleChange}
    />
  )
}
